import { Link } from "react-router-dom";
import ScrollToTop from "@/components/feature/ScrollToTop";
import BackToTop from "@/components/feature/BackToTop";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import FaqAccordion from "@/components/base/FaqAccordion";
import { useSeo, SITE_ORIGIN } from "@/hooks/useSeo";
import { faqList } from "@/mocks/faq";

const groups = [
  { id: "standard", name: "鉴定标准" },
  { id: "contribute", name: "投稿合作" },
  { id: "boundary", name: "内容边界" },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "FAQPage",
      name: "常见问题 · 瓜田爆料",
      url: `${SITE_ORIGIN}/faq`,
      inLanguage: "zh-CN",
      mainEntity: faqList.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
        { "@type": "ListItem", position: 2, name: "常见问题", item: `${SITE_ORIGIN}/faq` },
      ],
    },
  ],
};

export default function Faq() {
  useSeo({
    title: "常见问题 | 鉴定标准与内容边界 · 瓜田爆料",
    description:
      "瓜田爆料常见问题解答：可信度评分如何计算、四个结论标签的含义、弱证据与隐私边界如何处理、如何投稿线索以及多久会收到反馈。",
    keywords: ["常见问题", "鉴定标准", "可信度评分", "投稿合作", "内容边界"],
    path: "/faq",
    ogType: "website",
    jsonLd,
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <ScrollToTop />
      <SiteHeader />
      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100 paper-texture">
          <div className="max-w-shell mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "常见问题" }]} />
            <h1 className="mt-5 font-heading text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
              常见问题
            </h1>
            <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-600 leading-relaxed">
              关于评分怎么算、弱证据怎么处理、投稿之后会发生什么，这里给出了完整回答。
              找不到答案的话，欢迎直接联系我们。
            </p>
          </div>
        </section>

        <section className="max-w-shell mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-28">
                <h2 className="text-sm font-medium text-foreground-900 mb-3">问题分类</h2>
                <ul className="space-y-1">
                  {groups.map((g) => (
                    <li key={g.id}>
                      <a
                        href={`#${g.id}`}
                        className="flex items-center justify-between px-3 py-2 rounded-control text-sm text-foreground-600 hover:bg-background-100 hover:text-primary-600 transition-colors"
                      >
                        {g.name}
                        <span className="font-mono-num text-[11px] text-foreground-400">
                          {faqList.filter((f) => f.group === g.name).length}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-xs text-foreground-500 leading-relaxed">
                  仍有疑问？
                  <Link to="/contact" className="text-primary-600 hover:text-primary-700 ml-1">
                    联系我们
                  </Link>
                </p>
              </div>
            </aside>

            <div className="lg:col-span-9 space-y-12">
              {groups.map((g) => {
                const items = faqList.filter((f) => f.group === g.name);
                return (
                  <section key={g.id} id={g.id} className="scroll-mt-28">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
                      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                        {g.name}
                      </h2>
                    </div>
                    <FaqAccordion items={items} defaultOpen={null} />
                  </section>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}